import cx from 'classnames'
import { useMemo } from 'react'

import { SanityGrid } from '@data/sanity/queries/types/modules'
import { gridColorClasses } from '@lib/color'
import { getGridSizeClass, getItemOrderClass } from '@lib/dom'

import GridBlock from './grid-block'

type GridProps = SanityGrid

const Grid = ({
  size,
  columns,
  reverseSequence,
  style,
  background,
  noColumnGaps,
  noRowGaps,
}: GridProps) => {
  const parsedColumns = useMemo(() => {
    return columns.map((column, index) => {
      const columnClasses = column.sizes.map((columnSize) => {
        return getGridSizeClass(
          columnSize.breakpoint,
          columnSize.width,
          columnSize.justify,
          columnSize.align,
          columnSize.start,
        )
      })

      const orderClasses = column.sizes.map((columnSize) => {
        return getItemOrderClass(
          index,
          columnSize.breakpoint,
          columnSize.width,
          size,
          reverseSequence,
        )
      })

      return {
        ...column,
        classes: [...columnClasses, ...orderClasses],
      }
    })
  }, [])

  const fillStyle = 'bg-box-bg'
  const outlineStyle = 'border border-box-border'

  return (
    <section className={cx(background ? gridColorClasses[background] : '')}>
      <div className="relative container py-10 sm:py-12 md:py-16 lg:py-20">
        <div className="mx-auto">
          <div
            className={cx(`grid grid-cols-${size}`, {
              [`${fillStyle}`]: style === 'fill',
              [`${outlineStyle}`]: style === 'outline',
              'gap-x-4 sm:gap-x-8 lg:gap-x-12': !noColumnGaps,
              'gap-y-8 lg:gap-y-12': !noRowGaps,
              'gap-x-0': noColumnGaps,
              'gap-y-0': noRowGaps,
            })}
          >
            {parsedColumns.map((column) => (
              <div
                key={column._key}
                className={cx(column.classes, {
                  'px-6 py-6 sm:px-8 sm:py-10': style,
                  [`${fillStyle}`]: style === 'fill',
                  [`${outlineStyle}`]: style === 'outline',
                })}
              >
                {column.blocks.map((block) => (
                  <GridBlock key={block._key} block={block} />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Grid
